import { FormControl } from '@angular/forms';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';

import {
  IPrice,
  IListColumn,
  IListElementCell,
  isPriceCell,
  IListColumnPrice,
  IField,
  IListElementPriceCell,
  IList,
  TPeriod,
} from 'app/services/api5-service/api.interface';

import { ISPFormState } from 'common/dynamic-form/model';
import { getDropdownParentSelector } from 'common/dynamic-form/types/select/dropdown.utils';
import { formatCost, formatPercent } from 'common/formatting/formatting.utils';
import { CostFormattingOptions } from 'common/formatting/model';
import {
  IPriceDisplaySettings,
  PriceUi,
  PriceMessages,
} from 'common/price/model';
import { getTrackBy, isOnlyCostDisplayed } from 'common/price/price.utils';
import { DocHelper } from 'utils/dochelper';
import { RequiredField } from 'utils/required-field.type';

import { IListCellUi, ListCellPriceData } from '../../list-cell/model';

interface PaymentInfoMsgParams {
  cost: string;
  prefixMsg;
  period: TPeriod;
}

/**
 * Get initialization value for price cell
 *
 * @param cellData
 * @param state
 */
function getInitialValue(
  cellData: RequiredField<IListElementPriceCell, '$key'>,
  state: ISPFormState,
): TPeriod | undefined {
  const docModelValue = DocHelper.getStringValue(
    cellData.$key,
    state.docSubject.value,
  ) as TPeriod;
  const pricesDataInCell = DocHelper.getArrayWrapperValue(cellData.price);
  const allowPeriods = pricesDataInCell.map(priceData => priceData.period?.$);

  if (allowPeriods.includes(docModelValue)) {
    return docModelValue;
  } else {
    const firstOptionValue = pricesDataInCell[0].period?.$;
    const modelValue = state.formService.formControl(cellData.$key)?.value;
    return modelValue || firstOptionValue;
  }
}

/**
 * Get price form control. Append price form control to form model
 *
 * @param cellData
 * @param state
 */
function getPriceFormControl(
  cellData: RequiredField<IListElementPriceCell, '$key'>,
  state: ISPFormState,
): FormControl<TPeriod> {
  const initialValue = getInitialValue(cellData, state) as TPeriod;
  const formControl = state.formService.createFormControl(
    cellData.$key,
    initialValue,
    true,
  );

  return formControl;
}

/**
 * Get end text for cost for trial period as a number of days
 *
 * @param priceData
 */
function getCostSuffixForTrial(priceData: IPrice): string {
  if (!priceData.trial || !priceData.trial_msg) {
    return '';
  }

  return `${priceData.trial.$} ${priceData.trial_msg.$}`;
}

/**
 * Determine whether to display period for main cost or not
 *
 * @param priceData
 */
function isDisplayPeriodForMainCost(
  priceData: IPrice,
): priceData is RequiredField<IPrice, 'period'> {
  const needShowCalendarPaymentDetailsRow =
    !('prorata_hint' in priceData) && 'period_cost' in priceData;
  const isNeedPeriod =
    !needShowCalendarPaymentDetailsRow || priceData.period!.$ === '-100';
  const notShowFromBE =
    'period' in priceData && priceData.show_period?.$ !== 'off';

  return notShowFromBE && isNeedPeriod;
}

/**
 * Get end text for main cost for corresponding period
 *
 * @param priceData
 * @param messages
 */
function getSuffixForMainCost(
  priceData: IPrice,
  messages: PriceMessages,
): string {
  if (!isDisplayPeriodForMainCost(priceData)) {
    return '';
  }

  return (
    getCostSuffixForTrial(priceData) ||
    messages.periods[`period_${priceData.period.$}`].toLowerCase()
  );
}

/**
 * Get formatting options for main cost
 *
 * @param priceData
 * @param messages
 */
function getMainCostFormattingOptions(
  priceData: IPrice,
  messages: PriceMessages,
): CostFormattingOptions {
  const suffixMsg = getSuffixForMainCost(priceData, messages);
  const isNeedDisplayFreeMsg =
    !priceData.discount && !priceData.no_discount_cost;

  const formattingOptions = {
    sign: priceData.sign?.$ || 'auto',
    smallFraction: false,
    currency: priceData.currency.$,
    period: suffixMsg,
    freeMessage: isNeedDisplayFreeMsg ? messages.free : '',
  };
  return formattingOptions;
}

/**
 * Get formatted text of main cost as safe html
 *
 * @param priceData
 * @param messages
 * @param sanitizer
 */
function getMainCostHtml(
  priceData: IPrice,
  messages: PriceMessages,
  sanitizer: DomSanitizer,
): SafeHtml {
  const hasCalendarPaymentHint =
    'prorata_hint' in priceData && 'period_cost' in priceData;
  const mainCost = hasCalendarPaymentHint
    ? priceData.period_cost!.$
    : priceData.cost.$;
  const formattingOptions = getMainCostFormattingOptions(priceData, messages);

  return sanitizer.bypassSecurityTrustHtml(
    formatCost(mainCost, sanitizer, formattingOptions),
  );
}

/**
 * Get data to display main cost
 *
 * @param priceData
 * @param messages
 * @param sanitizer
 */
function getMainCostDataUi(
  priceData: IPrice,
  messages: PriceMessages,
  sanitizer: DomSanitizer,
): PriceUi['mainCostDataUi'] {
  const costHtml = getMainCostHtml(priceData, messages, sanitizer);

  return {
    costHtml,
    hintMsg: priceData.prorata_hint?.$ || '',
  };
}

/**
 * Get formatted text for old cost as safe html
 *
 * @param priceData
 * @param sanitizer
 */
function getOldCostHtml(
  priceData: IPrice,
  sanitizer: DomSanitizer,
): SafeHtml | undefined {
  if (!priceData.no_discount_cost) {
    return;
  }

  const optionsFormatting: CostFormattingOptions = {
    sign: 'auto',
    smallFraction: false,
    currency: priceData.currency.$,
    crossed: true,
    color: 'grey',
  };

  return sanitizer.bypassSecurityTrustHtml(
    formatCost(priceData.no_discount_cost.$, sanitizer, optionsFormatting),
  );
}

/**
 * Get formatted text for discount as safe html
 *
 * @param priceData
 * @param messages
 * @param sanitizer
 */
function getDiscountHtml(
  priceData: IPrice,
  messages: PriceMessages,
  sanitizer: DomSanitizer,
): SafeHtml | undefined {
  if (
    priceData.$special_price !== 'yes' &&
    !('discount_percent' in priceData)
  ) {
    return;
  }

  const discountHtml =
    priceData.$special_price === 'yes'
      ? messages.specialPrice
      : formatPercent(priceData.discount_percent!.$, sanitizer, {
          sign: 'negative',
        });

  return sanitizer.bypassSecurityTrustHtml(discountHtml);
}

/**
 * Get data to display discount
 *
 * @param priceData
 * @param messages
 * @param sanitizer
 */
function getDiscountDataUi(
  priceData: IPrice,
  messages: PriceMessages,
  sanitizer: DomSanitizer,
): PriceUi['discountDataUi'] {
  const oldCostHtml = getOldCostHtml(priceData, sanitizer);
  const discountHtml = getDiscountHtml(priceData, messages, sanitizer);

  return !oldCostHtml && !discountHtml
    ? undefined
    : {
        oldCostHtml: oldCostHtml,
        discountHtml,
      };
}

/**
 * Get parameters for generating information text for calendar payment
 *
 * @param priceData
 * @param messages
 */
function getParamsForMsgInfoCalendarPayment(
  priceData: IPrice,
  messages: PriceMessages,
): PaymentInfoMsgParams | undefined {
  if (!priceData.prorata_date || !priceData.period_cost || !priceData.period) {
    return;
  }

  const nextPaymentDate = priceData.prorata_date.$.replace(/-/g, '.');
  const prefixMsg = `${messages.after} ${nextPaymentDate}:`;

  return {
    prefixMsg,
    cost: priceData.period_cost.$,
    period: priceData.period.$,
  };
}

/**
 * Get parameters for generating information text for daily payment
 *
 * @param priceData
 * @param messages
 */
function getParamsForMsgInfoDaily(
  priceData: IPrice,
  messages: PriceMessages,
): PaymentInfoMsgParams | undefined {
  if (!priceData.daily) {
    return;
  }

  return {
    prefixMsg: messages.daily,
    cost: priceData.daily.$,
    period: '-50',
  };
}

/**
 * Get parameters for generating information text for trial period
 *
 * @param priceData
 * @param messages
 */
function getParamsForMsgInfoTrial(
  priceData: IPrice,
  messages: PriceMessages,
): PaymentInfoMsgParams | undefined {
  const hasCostAfterTrial =
    'price_after_trial' in priceData ||
    'price_after_trial_noautoprolong' in priceData;
  if (!('price_after_trial_period' in priceData) && !hasCostAfterTrial) {
    return;
  }

  const autoprolongEnabledForTrial = 'price_after_trial' in priceData;

  return autoprolongEnabledForTrial
    ? {
        prefixMsg: `${messages.afterTrial}:`,
        cost: priceData.price_after_trial!.$,
        period: priceData.price_after_trial_period!.$,
      }
    : {
        prefixMsg: `${messages.onMinimumPeriodAfterTrial}:`,
        cost: priceData.price_after_trial_noautoprolong!.$,
        period: priceData.price_after_trial_period!.$,
      };
}

/**
 * Get formatted text for payment information as safe html
 *
 * @param priceData
 * @param messages
 * @param sanitizer
 */
function getPaymentInfoHtml(
  priceData: IPrice,
  messages: PriceMessages,
  sanitizer: DomSanitizer,
): SafeHtml | undefined {
  const paymentInfoMsgParams: PaymentInfoMsgParams | undefined =
    getParamsForMsgInfoTrial(priceData, messages) ||
    getParamsForMsgInfoCalendarPayment(priceData, messages) ||
    getParamsForMsgInfoDaily(priceData, messages);
  if (!paymentInfoMsgParams) {
    return;
  }

  const formattingOptions: CostFormattingOptions = {
    sign: 'auto',
    smallFraction: false,
    currency: priceData.currency.$,
    period:
      messages.periods[`period_${paymentInfoMsgParams.period}`].toLowerCase(),
    bold: true,
  };
  const costHtml = formatCost(
    paymentInfoMsgParams.cost,
    sanitizer,
    formattingOptions,
  );

  return sanitizer.bypassSecurityTrustHtml(
    `${paymentInfoMsgParams.prefixMsg} ${costHtml}`,
  );
}

/**
 * Get data to display payment information
 *
 * @param priceData
 * @param messages
 * @param sanitizer
 */
function getPaymentInfoDataUi(
  priceData: IPrice,
  messages: PriceMessages,
  sanitizer: DomSanitizer,
): PriceUi['paymentInfoDataUi'] {
  const paymentInfoHtml = getPaymentInfoHtml(priceData, messages, sanitizer);
  const minPeriodMsg =
    priceData.min_period_msg && priceData.period?.$ !== '-100'
      ? priceData.min_period_msg.$
      : '';

  return !paymentInfoHtml && !minPeriodMsg
    ? undefined
    : {
        minPeriodMsg,
        paymentInfoHtml,
      };
}

/**
 * Get formatted text for tax as safe html
 *
 * @param priceData
 * @param messages
 * @param sanitizer
 */
function getTaxHtml(
  priceData: IPrice,
  messages: PriceMessages,
  sanitizer: DomSanitizer,
): SafeHtml | undefined {
  if (!priceData.tax_amount) {
    return;
  }

  const taxIncluded = priceData.tax_exclusive?.$ === 'off';
  const taxMsg = taxIncluded ? messages.taxIncluded : messages.taxExcluded;
  const optionsFormatting: CostFormattingOptions = {
    sign: 'auto',
    smallFraction: false,
    currency: priceData.currency.$,
    color: 'grey',
    prefix: taxMsg,
  };

  return sanitizer.bypassSecurityTrustHtml(
    formatCost(priceData.tax_amount.$, sanitizer, optionsFormatting),
  );
}

/**
 * Get data to display tax
 *
 * @param priceData
 * @param messages
 * @param sanitizer
 */
function getTaxDataUi(
  priceData: IPrice,
  messages: PriceMessages,
  sanitizer: DomSanitizer,
): PriceUi['taxDataUi'] {
  const taxHtml = getTaxHtml(priceData, messages, sanitizer);

  return !taxHtml
    ? undefined
    : {
        taxHtml: taxHtml,
      };
}

/**
 * Get price data from backend data
 *
 * @param priceData - be cell data
 * @param messages
 * @param sanitizer
 */
function getPriceDataUi(
  priceData: IPrice,
  messages: PriceMessages,
  sanitizer: DomSanitizer,
): PriceUi {
  const mainCostDataUi = getMainCostDataUi(priceData, messages, sanitizer);
  const discountDataUi = getDiscountDataUi(priceData, messages, sanitizer);
  const paymentInfoDataUi = getPaymentInfoDataUi(
    priceData,
    messages,
    sanitizer,
  );
  const taxDataUi = getTaxDataUi(priceData, messages, sanitizer);

  return {
    period: priceData.period?.$,
    trackBy: getTrackBy(priceData),
    mainCostDataUi,
    discountDataUi,
    paymentInfoDataUi,
    taxDataUi,
  };
}

/**
 * Get messages for prices from doc
 *
 * @param state
 */
function getPriceMessages(state: ISPFormState): PriceMessages {
  const doc = state.docSubject.value;
  const periodsMsg = {
    'period_-200': DocHelper.getMessage('period_-200', doc),
    'period_-100': DocHelper.getMessage('period_-100', doc),
    'period_-50': DocHelper.getMessage('period_-50', doc),
    period_0: DocHelper.getMessage('period_0', doc),
    period_1: DocHelper.getMessage('period_1', doc),
    period_3: DocHelper.getMessage('period_3', doc),
    period_6: DocHelper.getMessage('period_6', doc),
    period_12: DocHelper.getMessage('period_12', doc),
    period_24: DocHelper.getMessage('period_24', doc),
    period_36: DocHelper.getMessage('period_36', doc),
    period_48: DocHelper.getMessage('period_48', doc),
    period_60: DocHelper.getMessage('period_60', doc),
    period_120: DocHelper.getMessage('period_120', doc),
  };
  const afterMsg = DocHelper.getMessage('price_after', doc);
  const whenPaidForPeriodMsg = DocHelper.getMessage(
    'price_paid_for_period',
    doc,
  );
  const dailyMsg = DocHelper.getMessage('billdaily', doc);
  const whenPaidMsg = DocHelper.getMessage('price_paid', doc);
  const nextMsg = DocHelper.getMessage('price_next', doc);
  const taxExcludedMsg = DocHelper.getMessage('price_tax_exclusive_on', doc);
  const taxIncludedMsg = DocHelper.getMessage('price_tax_exclusive_off', doc);
  const trialAfterMsg = DocHelper.getMessage('price_after_trial', doc);
  const onMinimumPeriodAfterTrialMsg = DocHelper.getMessage(
    'price_after_trial_noautoprolong',
    doc,
  );
  const specialPriceMsg = DocHelper.getMessage('price_special_price', doc);
  const freeMsg = DocHelper.getMessage('price_free', doc);
  const forMsg = DocHelper.getMessage('price_for', doc);

  return {
    periods: periodsMsg,
    taxExcluded: taxExcludedMsg,
    taxIncluded: taxIncludedMsg,
    after: afterMsg,
    next: nextMsg,
    afterTrial: trialAfterMsg,
    onMinimumPeriodAfterTrial: onMinimumPeriodAfterTrialMsg,
    specialPrice: specialPriceMsg,
    whenPaidForPeriod: whenPaidForPeriodMsg,
    whenPaid: whenPaidMsg,
    daily: dailyMsg,
    free: freeMsg,
    for: forMsg,
  };
}

/**
 * Get generated cost data for a cell
 *
 * @param cellData
 * @param state
 * @param cell
 * @param column
 * @param field
 * @param control
 */
function getCellPriceData(
  cellData: IListElementPriceCell,
  state: ISPFormState,
  cell: IListCellUi,
  column: IListColumn,
  field: IField,
  control: IList,
): ListCellPriceData {
  const pricesDataInCell = DocHelper.getArrayWrapperValue(cellData.price);
  const messages: PriceMessages = getPriceMessages(state);
  const pricesData: PriceUi[] = pricesDataInCell.map<PriceUi>(priceData =>
    getPriceDataUi(priceData, messages, state.sanitizer),
  );
  const displaySettings: IPriceDisplaySettings = {
    mode:
      cellData.$display_type ||
      (column as IListColumnPrice).$display_type ||
      'small',
    align: column.$align || 'left',
    font: {
      color: cell.color,
      size: cell.size,
      weight: cell.weight,
    },
  };

  return {
    data: pricesData,
    displaySettings,
    messages,
    dropdownParentSelector: getDropdownParentSelector(state, control, field),
  };
}

/**
 * Get price cell with specific data
 *
 * @param control
 * @param field
 * @param cell - FE cell base metadata
 * @param column - column meta
 * @param state - form state
 * @param cellData - BE data of cell
 */
export function getPriceCellWithData(
  control: IList,
  field: IField,
  cell: IListCellUi,
  column: IListColumn,
  state: ISPFormState,
  cellData: IListElementCell,
): IListCellUi {
  if (!isPriceCell(cellData)) {
    return cell;
  }

  cell.priceData = getCellPriceData(
    cellData,
    state,
    cell,
    column,
    field,
    control,
  );
  cell.trackBy = cell.priceData.data.map(({ trackBy }) => trackBy).join('');

  if (!cell.verticalAlign) {
    cell.verticalAlign =
      cell.priceData.displaySettings.mode === 'full' &&
      !isOnlyCostDisplayed(cell.priceData.data[0])
        ? 'top'
        : 'baseline';
  }

  if (cellData.$key) {
    cell.priceData.formControlKey = cellData.$key;
    cell.priceData.formControl = getPriceFormControl(
      cellData as RequiredField<IListElementPriceCell, '$key'>,
      state,
    );

    if (cell.conditional) {
      state.conditionService.handleConditions(
        cell.conditional,
        cell.priceData.formControl.value,
      );
    }
  }

  return cell;
}
